import { useRef, useMemo, useEffect } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { FIRE_COLORS } from '../BlazeMaterial';

// Shared geometries - created once, reused
const sharedBoxGeometry = new THREE.BoxGeometry(0.85, 0.85, 0.4);
const sharedFireGeometry = new THREE.BoxGeometry(0.9, 0.9, 0.3);
const sharedLightningGeometry = new THREE.RingGeometry(0.3, 0.5, 4);
const sharedSmokeGeometry = new THREE.CircleGeometry(0.25, 8);
const sharedSparkGeometry = new THREE.CircleGeometry(1, 4);
const sharedSweepGeometry = new THREE.PlaneGeometry(1.5, 1.1);
const sharedFlashGeometry = new THREE.PlaneGeometry(11, 1.1);

// Temp objects for matrix calculations
const tempMatrix = new THREE.Matrix4();
const tempPosition = new THREE.Vector3();
const tempScale = new THREE.Vector3();
const tempQuaternion = new THREE.Quaternion();
const tempEuler = new THREE.Euler();

const EmberLineClearEffect = ({ rows = [], onComplete }) => {
  const startTime = useRef(null);

  // Refs for instanced meshes
  const blockInstanceRef = useRef();
  const fireInstanceRef = useRef();
  const lightningInstanceRef = useRef();
  const smokeInstanceRef = useRef();
  const sparkInstanceRef = useRef();
  const sweepInstanceRef = useRef();
  const flashInstanceRef = useRef();

  // Ensure we have valid rows to work with
  const validRows = rows && rows.length > 0 ? rows : [19];

  // Create spinning blocks data
  const spinningBlocks = useMemo(() => {
    const blocks = [];
    validRows.forEach((row, rowIndex) => {
      for (let col = 0; col < 10; col++) {
        blocks.push({
          position: [col - 4.5, 20 - row - 0.5, 0.2],
          delay: rowIndex * 0.05 + col * 0.02,
          spinDirection: col < 5 ? 1 : -1,
          rotation: [0, 0, 0],
        });
      }
    });
    return blocks;
  }, [validRows]);

  // Spark bursts data
  const sparkBursts = useMemo(() => {
    const bursts = [];
    validRows.forEach((row, rowIndex) => {
      for (let col = 0; col < 10; col += 2) {
        for (let i = 0; i < 6; i++) {
          const angle = (i / 6) * Math.PI * 2 + Math.random() * 0.5;
          bursts.push({
            x: col - 4.5,
            y: 20 - row - 0.5,
            angle,
            speed: 2 + Math.random() * 2,
            size: 0.03 + Math.random() * 0.02,
            life: 0.3 + Math.random() * 0.2,
            delay: rowIndex * 0.05 + col * 0.02 + 0.1,
            isCore: i % 2 === 0,
          });
        }
      }
    });
    return bursts;
  }, [validRows]);

  // Fire sweeps per row
  const fireSweeps = useMemo(() => {
    return validRows.map((row, i) => ({
      row,
      y: 20 - row - 0.5,
      delay: i * 0.05,
    }));
  }, [validRows]);

  // Row flashes
  const rowFlashes = useMemo(() => {
    return validRows.map((row, i) => ({
      row,
      y: 20 - row - 0.5,
      delay: i * 0.05,
    }));
  }, [validRows]);

  // Smoke data (8 per row)
  const smokeData = useMemo(() => {
    const smokes = [];
    validRows.forEach((row, rowIndex) => {
      for (let i = 0; i < 8; i++) {
        smokes.push({
          x: (i / 7) * 10 - 5,
          y: 20 - row - 0.5,
          size: 0.2 + Math.random() * 0.15,
          speed: 0.5 + Math.random() * 0.3,
          drift: (Math.random() - 0.5) * 0.3,
          delay: rowIndex * 0.05 + 0.2,
        });
      }
    });
    return smokes;
  }, [validRows]);

  // Materials
  const blockMaterial = useMemo(() => new THREE.MeshStandardMaterial({
    color: FIRE_COLORS.primary,
    emissive: FIRE_COLORS.ember,
    emissiveIntensity: 1,
    transparent: true,
    opacity: 0.95,
    metalness: 0.3,
    roughness: 0.5,
  }), []);

  const fireMaterial = useMemo(() => new THREE.MeshBasicMaterial({
    color: FIRE_COLORS.flame,
    transparent: true,
    opacity: 0.7,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
    side: THREE.DoubleSide,
  }), []);

  const lightningMaterial = useMemo(() => new THREE.MeshBasicMaterial({
    color: FIRE_COLORS.flameCore,
    transparent: true,
    opacity: 0.9,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
  }), []);

  const smokeMaterial = useMemo(() => new THREE.MeshBasicMaterial({
    color: FIRE_COLORS.smoke,
    transparent: true,
    opacity: 0.35,
    depthWrite: false,
  }), []);

  const sparkCoreMaterial = useMemo(() => new THREE.MeshBasicMaterial({
    color: FIRE_COLORS.flameCore,
    transparent: true,
    opacity: 0.9,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
  }), []);

  const sweepMaterial = useMemo(() => new THREE.MeshBasicMaterial({
    color: FIRE_COLORS.flame,
    transparent: true,
    opacity: 0.8,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
  }), []);

  const flashMaterial = useMemo(() => new THREE.MeshBasicMaterial({
    color: FIRE_COLORS.flameCore,
    transparent: true,
    opacity: 0.6,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
  }), []);

  // Initialize instances
  useEffect(() => {
    // Initialize blocks
    if (blockInstanceRef.current) {
      spinningBlocks.forEach((block, i) => {
        tempPosition.set(...block.position);
        tempScale.setScalar(0);
        tempMatrix.compose(tempPosition, tempQuaternion, tempScale);
        blockInstanceRef.current.setMatrixAt(i, tempMatrix);
      });
      blockInstanceRef.current.instanceMatrix.needsUpdate = true;
    }

    // Initialize fire
    if (fireInstanceRef.current) {
      spinningBlocks.forEach((_, i) => {
        tempScale.setScalar(0);
        tempMatrix.compose(tempPosition, tempQuaternion, tempScale);
        fireInstanceRef.current.setMatrixAt(i, tempMatrix);
      });
      fireInstanceRef.current.instanceMatrix.needsUpdate = true;
    }

    // Initialize lightning
    if (lightningInstanceRef.current) {
      spinningBlocks.forEach((_, i) => {
        tempScale.setScalar(0);
        tempMatrix.compose(tempPosition, tempQuaternion, tempScale);
        lightningInstanceRef.current.setMatrixAt(i, tempMatrix);
      });
      lightningInstanceRef.current.instanceMatrix.needsUpdate = true;
    }

    // Initialize smoke
    if (smokeInstanceRef.current) {
      smokeData.forEach((smoke, i) => {
        tempPosition.set(smoke.x, smoke.y, 0.1);
        tempScale.setScalar(0);
        tempMatrix.compose(tempPosition, tempQuaternion, tempScale);
        smokeInstanceRef.current.setMatrixAt(i, tempMatrix);
      });
      smokeInstanceRef.current.instanceMatrix.needsUpdate = true;
    }

    // Initialize sparks
    if (sparkInstanceRef.current) {
      sparkBursts.forEach((spark, i) => {
        tempPosition.set(spark.x, spark.y, 0.2);
        tempScale.setScalar(0);
        tempMatrix.compose(tempPosition, tempQuaternion, tempScale);
        sparkInstanceRef.current.setMatrixAt(i, tempMatrix);
      });
      sparkInstanceRef.current.instanceMatrix.needsUpdate = true;
    }

    // Initialize sweeps
    if (sweepInstanceRef.current) {
      fireSweeps.forEach((sweep, i) => {
        tempPosition.set(0, sweep.y, 0.25);
        tempScale.setScalar(0);
        tempMatrix.compose(tempPosition, tempQuaternion, tempScale);
        sweepInstanceRef.current.setMatrixAt(i, tempMatrix);
      });
      sweepInstanceRef.current.instanceMatrix.needsUpdate = true;
    }

    // Initialize flashes
    if (flashInstanceRef.current) {
      rowFlashes.forEach((flash, i) => {
        tempPosition.set(0, flash.y, 0.3);
        tempScale.setScalar(0);
        tempMatrix.compose(tempPosition, tempQuaternion, tempScale);
        flashInstanceRef.current.setMatrixAt(i, tempMatrix);
      });
      flashInstanceRef.current.instanceMatrix.needsUpdate = true;
    }
  }, [spinningBlocks, sparkBursts, fireSweeps, rowFlashes, smokeData]);

  // Single consolidated useFrame for ALL animations
  useFrame((state) => {
    if (startTime.current === null) {
      startTime.current = state.clock.elapsedTime;
    }

    const elapsed = state.clock.elapsedTime - startTime.current;

    if (elapsed > 0.7) {
      onComplete?.();
      return;
    }

    // Animate spinning blocks
    if (blockInstanceRef.current) {
      spinningBlocks.forEach((block, i) => {
        const adjustedTime = elapsed - block.delay;

        if (adjustedTime <= 0) {
          tempScale.setScalar(0);
          tempMatrix.compose(tempPosition, tempQuaternion, tempScale);
          blockInstanceRef.current.setMatrixAt(i, tempMatrix);
          if (fireInstanceRef.current) fireInstanceRef.current.setMatrixAt(i, tempMatrix);
          if (lightningInstanceRef.current) lightningInstanceRef.current.setMatrixAt(i, tempMatrix);
          return;
        }

        if (adjustedTime > 0.5) {
          tempScale.setScalar(0);
          tempMatrix.compose(tempPosition, tempQuaternion, tempScale);
          blockInstanceRef.current.setMatrixAt(i, tempMatrix);
          if (fireInstanceRef.current) fireInstanceRef.current.setMatrixAt(i, tempMatrix);
          if (lightningInstanceRef.current) lightningInstanceRef.current.setMatrixAt(i, tempMatrix);
          return;
        }

        const progress = adjustedTime / 0.5;

        // Rotation
        const rotSpeed = progress * progress * 15;
        block.rotation[2] += block.spinDirection * rotSpeed * 0.05;
        block.rotation[0] += rotSpeed * 0.02;

        // Scale shrinks
        const scale = Math.max(0, 1 - progress * 1.1);

        // Drift toward center
        const driftX = block.position[0] * (1 - progress * 0.3);

        tempPosition.set(driftX, block.position[1], block.position[2]);
        tempEuler.set(block.rotation[0], 0, block.rotation[2]);
        tempQuaternion.setFromEuler(tempEuler);
        tempScale.setScalar(scale);
        tempMatrix.compose(tempPosition, tempQuaternion, tempScale);
        blockInstanceRef.current.setMatrixAt(i, tempMatrix);

        // Fire envelope
        if (fireInstanceRef.current && progress > 0.1 && progress < 0.9) {
          tempPosition.set(driftX, block.position[1], block.position[2] + 0.1);
          tempScale.setScalar(scale * 1.3);
          tempMatrix.compose(tempPosition, tempQuaternion, tempScale);
          fireInstanceRef.current.setMatrixAt(i, tempMatrix);
        } else if (fireInstanceRef.current) {
          tempScale.setScalar(0);
          tempMatrix.compose(tempPosition, tempQuaternion, tempScale);
          fireInstanceRef.current.setMatrixAt(i, tempMatrix);
        }

        // Lightning crackle
        if (lightningInstanceRef.current) {
          const showLightning = Math.sin(adjustedTime * 40) > 0.7 && progress > 0.2 && progress < 0.8;
          if (showLightning) {
            tempPosition.set(driftX, block.position[1], block.position[2] + 0.15);
            tempScale.setScalar(scale * 1.5);
            tempEuler.set(0, 0, Math.random() * Math.PI * 2);
            tempQuaternion.setFromEuler(tempEuler);
            tempMatrix.compose(tempPosition, tempQuaternion, tempScale);
            lightningInstanceRef.current.setMatrixAt(i, tempMatrix);
          } else {
            tempScale.setScalar(0);
            tempMatrix.compose(tempPosition, tempQuaternion, tempScale);
            lightningInstanceRef.current.setMatrixAt(i, tempMatrix);
          }
        }
      });
      blockInstanceRef.current.instanceMatrix.needsUpdate = true;
      if (fireInstanceRef.current) fireInstanceRef.current.instanceMatrix.needsUpdate = true;
      if (lightningInstanceRef.current) lightningInstanceRef.current.instanceMatrix.needsUpdate = true;
    }

    // Animate smoke
    if (smokeInstanceRef.current) {
      smokeData.forEach((smoke, i) => {
        const adjustedTime = elapsed - smoke.delay;

        if (adjustedTime <= 0 || adjustedTime > 0.6) {
          tempScale.setScalar(0);
          tempMatrix.compose(tempPosition, tempQuaternion, tempScale);
          smokeInstanceRef.current.setMatrixAt(i, tempMatrix);
          return;
        }

        const progress = adjustedTime / 0.6;
        const x = smoke.x + Math.sin(adjustedTime * 2 + i) * smoke.drift;
        const y = smoke.y + progress * smoke.speed;
        const scale = smoke.size * (1 + progress);

        tempPosition.set(x, y, 0.1);
        tempScale.setScalar(scale);
        tempQuaternion.identity();
        tempMatrix.compose(tempPosition, tempQuaternion, tempScale);
        smokeInstanceRef.current.setMatrixAt(i, tempMatrix);
      });
      smokeInstanceRef.current.instanceMatrix.needsUpdate = true;
      smokeInstanceRef.current.material.opacity = Math.max(0, 0.35 * (1 - elapsed / 0.6));
    }

    // Animate sparks
    if (sparkInstanceRef.current) {
      sparkBursts.forEach((spark, i) => {
        const adjustedTime = elapsed - spark.delay;

        if (adjustedTime <= 0 || adjustedTime > spark.life) {
          tempScale.setScalar(0);
          tempMatrix.compose(tempPosition, tempQuaternion, tempScale);
          sparkInstanceRef.current.setMatrixAt(i, tempMatrix);
          return;
        }

        const progress = adjustedTime / spark.life;
        const distance = spark.speed * adjustedTime;
        const x = spark.x + Math.cos(spark.angle) * distance;
        const y = spark.y + Math.sin(spark.angle) * distance;
        const scale = spark.size * (1 - progress);

        tempPosition.set(x, y, 0.2);
        tempScale.setScalar(scale);
        tempQuaternion.identity();
        tempMatrix.compose(tempPosition, tempQuaternion, tempScale);
        sparkInstanceRef.current.setMatrixAt(i, tempMatrix);
      });
      sparkInstanceRef.current.instanceMatrix.needsUpdate = true;
    }

    // Animate fire sweeps
    if (sweepInstanceRef.current) {
      fireSweeps.forEach((sweep, i) => {
        const adjustedTime = elapsed - sweep.delay;

        if (adjustedTime <= 0 || adjustedTime > 0.25) {
          tempScale.setScalar(0);
          tempMatrix.compose(tempPosition, tempQuaternion, tempScale);
          sweepInstanceRef.current.setMatrixAt(i, tempMatrix);
          return;
        }

        const progress = adjustedTime / 0.25;
        const xPos = -6 + progress * 12;
        const scaleX = 1.5 + Math.sin(progress * Math.PI) * 0.5;

        tempPosition.set(xPos, sweep.y, 0.25);
        tempScale.set(scaleX, 1, 1);
        tempQuaternion.identity();
        tempMatrix.compose(tempPosition, tempQuaternion, tempScale);
        sweepInstanceRef.current.setMatrixAt(i, tempMatrix);
      });
      sweepInstanceRef.current.instanceMatrix.needsUpdate = true;
      sweepInstanceRef.current.material.opacity = Math.sin((elapsed / 0.25) * Math.PI) * 0.8;
    }

    // Animate row flashes
    if (flashInstanceRef.current) {
      rowFlashes.forEach((flash, i) => {
        const adjustedTime = elapsed - flash.delay;

        if (adjustedTime <= 0 || adjustedTime > 0.15) {
          tempScale.set(1, 0, 1);
          tempMatrix.compose(tempPosition, tempQuaternion, tempScale);
          flashInstanceRef.current.setMatrixAt(i, tempMatrix);
          return;
        }

        tempPosition.set(0, flash.y, 0.3);
        tempScale.set(1, 1, 1);
        tempQuaternion.identity();
        tempMatrix.compose(tempPosition, tempQuaternion, tempScale);
        flashInstanceRef.current.setMatrixAt(i, tempMatrix);
      });
      flashInstanceRef.current.instanceMatrix.needsUpdate = true;
      flashInstanceRef.current.material.opacity = Math.sin((elapsed / 0.15) * Math.PI) * 0.6;
    }
  });

  return (
    <group>
      {/* Row flashes - Instanced */}
      {rowFlashes.length > 0 && (
        <instancedMesh
          ref={flashInstanceRef}
          args={[sharedFlashGeometry, flashMaterial, rowFlashes.length]}
          frustumCulled={false}
        />
      )}

      {/* Fire sweeps - Instanced */}
      {fireSweeps.length > 0 && (
        <instancedMesh
          ref={sweepInstanceRef}
          args={[sharedSweepGeometry, sweepMaterial, fireSweeps.length]}
          frustumCulled={false}
        />
      )}

      {/* Spinning blocks - Instanced */}
      {spinningBlocks.length > 0 && (
        <>
          <instancedMesh
            ref={blockInstanceRef}
            args={[sharedBoxGeometry, blockMaterial, spinningBlocks.length]}
            frustumCulled={false}
          />
          <instancedMesh
            ref={fireInstanceRef}
            args={[sharedFireGeometry, fireMaterial, spinningBlocks.length]}
            frustumCulled={false}
          />
          <instancedMesh
            ref={lightningInstanceRef}
            args={[sharedLightningGeometry, lightningMaterial, spinningBlocks.length]}
            frustumCulled={false}
          />
        </>
      )}

      {/* Sparks - Instanced */}
      {sparkBursts.length > 0 && (
        <instancedMesh
          ref={sparkInstanceRef}
          args={[sharedSparkGeometry, sparkCoreMaterial, sparkBursts.length]}
          frustumCulled={false}
        />
      )}

      {/* Smoke - Instanced */}
      {smokeData.length > 0 && (
        <instancedMesh
          ref={smokeInstanceRef}
          args={[sharedSmokeGeometry, smokeMaterial, smokeData.length]}
          frustumCulled={false}
        />
      )}
    </group>
  );
};

export default EmberLineClearEffect;
